import { ChevronDownIcon } from '@chakra-ui/icons';
import { Box, Button, Divider, Flex, Switch, Text } from '@chakra-ui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState } from 'react';

import { Loading } from '@revelio/core';

import { EconomicImpactMap } from './components/EconomicImpactMap';
import { GeoTreePicker } from './components/GeoTreePicker';
import { OutputTab } from './components/OutputTab';
import { PlaceholderTab } from './components/PlaceholderTab';
import { ScenarioChip } from './components/ScenarioChip';
import { ScenarioPanel } from './components/ScenarioPanel';
import { type ViewTab, ViewTabStrip } from './components/ViewTabStrip';
import { impactRequestForGeo } from './data/us-geo';
import type { Scenario } from './types';
import { useEconomicImpactUrlState } from './url-state';
import { useImpactScenarios } from './useImpact';

// Page-scoped client: the app doesn't mount a global QueryClientProvider
// (user-preferences drives its own imperative QueryClient), so the
// provider lives here to keep the beta page self-contained.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  },
});

function EconomicImpactPageContent() {
  // Geography + scenarios are deeplinked (see url-state); the rest is
  // ephemeral view state.
  const { selectedGeo, setSelectedGeo, scenarios, setScenarios } =
    useEconomicImpactUrlState();
  const [activeTab, setActiveTab] = useState<ViewTab>('Output');
  const [demoMode, setDemoMode] = useState(false);
  // Picking a geography on the map opens the scenario form (prefilled with it)
  // so the next step is obvious.
  const [addOpen, setAddOpen] = useState(false);

  const impactQuery = useImpactScenarios({
    demoMode,
    // Re-rolls the demo charts when the output geography changes (demo only).
    seed: selectedGeo?.fips,
    scenarios: scenarios.map((scenario) => {
      const request = impactRequestForGeo(scenario.geo);
      return {
        industryId: scenario.industryId,
        amount: scenario.amount,
        fips: request.fips,
        level: request.level,
      };
    }),
  });

  function handleAddScenario(scenario: Scenario) {
    setScenarios((prev) => [...prev, scenario]);
    // Adopt the scenario's geo as the page's primary only if none is set
    // yet — never override an existing title selection.
    setSelectedGeo((current) => current ?? scenario.geo);
  }

  function handleRemoveScenario(id: string) {
    setScenarios((prev) => prev.filter((scenario) => scenario.id !== id));
  }

  function handleUpdateScenario(id: string, draft: Omit<Scenario, 'id'>) {
    setScenarios((prev) =>
      prev.map((scenario) =>
        scenario.id === id ? { ...scenario, ...draft } : scenario
      )
    );
  }

  const showResults = scenarios.length > 0;
  const resultsContent =
    activeTab === 'Output' ? (
      <OutputTab
        impactQuery={impactQuery}
        selectedGeo={selectedGeo}
        onSelectGeo={setSelectedGeo}
      />
    ) : (
      <PlaceholderTab tabName={activeTab} />
    );
  // While any scenario is in flight, cover the results with the Revelio loader
  // rather than showing a half-resolved page (stale charts, dashed topline).
  const results = impactQuery.isFetching ? (
    <Flex
      flex="1"
      minH="0"
      align="center"
      justify="center"
      data-testid="economic-impact-loading"
    >
      <Loading />
    </Flex>
  ) : (
    resultsContent
  );

  return (
    <Flex
      direction="column"
      height="100vh"
      maxH="100vh"
      overflow="hidden"
      p="0.5rem 1.5rem 1rem 1.5rem"
    >
      {/* Title reads "Economic Impact on <geography>", where the geography is
          a searchable state→county tree picker rendered inline as the title's
          trailing word. */}
      <Flex
        alignItems="center"
        mb={2}
        minH="32px"
        flexShrink={0}
        gap="6px"
        data-testid="economic-impact-header"
      >
        <Flex align="center" gap="2px">
          <Text fontSize="1.25rem" fontWeight="600" lineHeight="32px">
            Economic Impact on
          </Text>
          <GeoTreePicker selectedGeo={selectedGeo} onSelect={setSelectedGeo}>
            <Button
              variant="ghost"
              pl="4px"
              pr="6px"
              h="32px"
              fontSize="1.25rem"
              fontWeight="600"
              color={selectedGeo ? 'navyBlue.500' : 'gray.400'}
              rightIcon={<ChevronDownIcon color="gray.400" boxSize="20px" />}
              data-testid="geography-title-trigger"
            >
              {selectedGeo?.name ?? 'Select geography'}
            </Button>
          </GeoTreePicker>
        </Flex>
        <Box flex="1" />
        {showResults && (
          <>
            <ViewTabStrip active={activeTab} onSelect={setActiveTab} />
            <Box alignSelf="center" w="1px" h="20px" bg="gray.200" mx="16px" />
          </>
        )}
        <Flex align="center" gap="8px">
          <Switch
            size="sm"
            colorScheme="green"
            isChecked={demoMode}
            onChange={(e) => setDemoMode(e.target.checked)}
            data-testid="demo-data-toggle"
          />
          <Text fontSize="12.5px" fontWeight="500" color="gray.500">
            Demo data
          </Text>
        </Flex>
      </Flex>
      <Divider flexShrink={0} />

      {/* Scenario chips + add trigger (design FilterBar layout). The view
          tabs live up in the header beside the demo toggle. */}
      <Flex
        align="center"
        gap="16px"
        rowGap="10px"
        flexWrap="wrap"
        pt="0.75rem"
        pb="0.75rem"
        flexShrink={0}
      >
        {scenarios.map((scenario) => (
          <ScenarioChip
            key={scenario.id}
            scenario={scenario}
            onUpdate={(draft) => handleUpdateScenario(scenario.id, draft)}
            onRemove={() => handleRemoveScenario(scenario.id)}
          />
        ))}
        <ScenarioPanel
          defaultGeo={selectedGeo}
          onAddScenario={handleAddScenario}
          isOpen={addOpen}
          onOpenChange={setAddOpen}
        />
      </Flex>

      {showResults ? (
        results
      ) : (
        <Flex direction="column" gap="14px" flex="1" minH="0">
          <Box
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="8px"
            p="14px"
            flex="1"
            minH="0"
            display="flex"
          >
            <Box
              position="relative"
              flex="1"
              minH="0"
              borderRadius="6px"
              overflow="hidden"
            >
              <EconomicImpactMap
                selectedGeo={selectedGeo}
                onSelectGeo={(geo) => {
                  setSelectedGeo(geo);
                  // Open the scenario form as soon as a geography is chosen.
                  setAddOpen(true);
                }}
              />
            </Box>
          </Box>
        </Flex>
      )}
    </Flex>
  );
}

export function EconomicImpactPage() {
  return (
    <QueryClientProvider client={queryClient}>
      <EconomicImpactPageContent />
    </QueryClientProvider>
  );
}
